/**
 * Authentication Configuration Utility
 *
 * This module provides environment-based authentication configuration for the application.
 * It supports multiple authentication providers (Firebase, Keycloak) and methods (Google OAuth)
 * that can be enabled/disabled via environment variables.
 *
 * Key Features:
 * - Centralized authentication configuration
 * - Environment-based provider selection
 * - Runtime validation of required credentials
 * - Helper functions for conditional auth initialization
 *
 * Environment Variables:
 * - VITE_AUTH_PROVIDER: Primary auth provider ('firebase' | 'keycloak')
 * - VITE_ALLOW_KEYCLOAK: Enable Keycloak SSO ('true' | 'false')
 * - VITE_ALLOW_FIREBASE: Enable Firebase Auth ('true' | 'false')
 * - VITE_ALLOW_GOOGLE: Enable Google OAuth ('true' | 'false')
 *
 * Usage:
 * ```typescript
 * import { getAuthConfig, shouldEnableKeycloak } from '@/config/authConfig';
 *
 * // Get current configuration
 * const config = getAuthConfig();
 *
 * // Conditionally initialize auth providers
 * if (shouldEnableKeycloak()) {
 *   await initializeKeycloak();
 * }
 * ```
 *
 * @module authConfig
 */

/**
 * Supported authentication providers
 */
export type AuthProvider = 'firebase' | 'keycloak' | 'hybrid';

/**
 * Authentication configuration interface
 *
 * Defines the structure of the authentication configuration object
 * that controls which auth providers and methods are enabled.
 */
export interface AuthConfig {
  /** Primary authentication provider to use */
  provider: AuthProvider;

  /** Whether Keycloak SSO is enabled */
  allowKeycloak: boolean;

  /** Whether Firebase Authentication is enabled */
  allowFirebase: boolean;

  /** Whether Google OAuth sign-in is enabled */
  allowGoogle: boolean;

  /** Keycloak configuration (only populated if Keycloak is enabled) */
  keycloak?: {
    baseUrl: string;
    realm: string;
    clientId: string;
  };

  /** Firebase configuration (only populated if Firebase is enabled) */
  firebase?: {
    projectId: string;
    apiKey: string;
  };
}

/**
 * Validation error thrown when authentication configuration is invalid
 */
export class AuthConfigError extends Error {
  constructor(message: string) {
    super(`Authentication Configuration Error: ${message}`);
    this.name = 'AuthConfigError';
  }
}

/**
 * Parses a boolean environment variable
 *
 * Converts string environment variables to boolean values.
 * Defaults to false if the variable is not set or invalid.
 *
 * @param value - Environment variable value
 * @param defaultValue - Default value if parsing fails
 * @returns Parsed boolean value
 */
function parseBoolean(value: string | undefined, defaultValue = false): boolean {
  if (!value) return defaultValue;
  return value.toLowerCase() === 'true';
}

/**
 * Gets the current authentication configuration from environment variables
 *
 * Reads authentication settings from Vite environment variables and constructs
 * a configuration object. This function should be called during application
 * initialization to determine which auth providers to enable.
 *
 * @returns Current authentication configuration
 *
 * @example
 * ```typescript
 * const config = getAuthConfig();
 * console.log(`Using ${config.provider} as primary auth provider`);
 * console.log(`Keycloak enabled: ${config.allowKeycloak}`);
 * ```
 */
export function getAuthConfig(): AuthConfig {
  // Read primary auth provider (defaults to 'firebase' for backward compatibility)
  const provider = (import.meta.env.VITE_AUTH_PROVIDER as AuthProvider) || 'firebase';

  // Determine which auth methods to enable based on provider setting
  let allowKeycloak = false;
  let allowFirebase = false;

  if (provider === 'hybrid') {
    // Hybrid mode: enable both Firebase and Keycloak
    allowKeycloak = true;
    allowFirebase = true;
  } else if (provider === 'keycloak') {
    // Keycloak only mode
    allowKeycloak = true;
    allowFirebase = false;
  } else {
    // Firebase only mode (default)
    allowKeycloak = false;
    allowFirebase = true;
  }

  // Allow override via explicit environment variables if needed
  if (import.meta.env.VITE_ALLOW_KEYCLOAK !== undefined) {
    allowKeycloak = parseBoolean(import.meta.env.VITE_ALLOW_KEYCLOAK);
  }
  if (import.meta.env.VITE_ALLOW_FIREBASE !== undefined) {
    allowFirebase = parseBoolean(import.meta.env.VITE_ALLOW_FIREBASE);
  }

  // Google OAuth is optional and defaults to enabled for Firebase/Hybrid modes
  const allowGoogle = parseBoolean(
    import.meta.env.VITE_ALLOW_GOOGLE,
    allowFirebase // Default to enabled if Firebase is enabled
  );

  console.log('[AuthConfig] Loading authentication configuration:', {
    provider,
    allowKeycloak,
    allowFirebase,
    allowGoogle,
  });

  // Build base configuration
  const config: AuthConfig = {
    provider,
    allowKeycloak,
    allowFirebase,
    allowGoogle,
  };

  // Add Keycloak configuration if enabled
  if (allowKeycloak) {
    const baseUrl = import.meta.env.VITE_KEYCLOAK_BASE_URL;
    const realm = import.meta.env.VITE_KEYCLOAK_REALM;
    const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID;

    if (baseUrl && realm && clientId) {
      config.keycloak = {
        baseUrl,
        realm,
        clientId,
      };
      console.log('[AuthConfig] Keycloak configuration loaded:', {
        baseUrl,
        realm,
        clientId,
      });
    } else {
      console.warn('[AuthConfig] Keycloak enabled but configuration incomplete:', {
        hasBaseUrl: !!baseUrl,
        hasRealm: !!realm,
        hasClientId: !!clientId,
      });
    }
  }

  // Add Firebase configuration if enabled
  if (allowFirebase) {
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
    const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;

    if (projectId && apiKey) {
      config.firebase = {
        projectId,
        apiKey,
      };
      console.log('[AuthConfig] Firebase configuration loaded:', {
        projectId,
        hasApiKey: !!apiKey,
      });
    } else {
      console.warn('[AuthConfig] Firebase enabled but configuration incomplete:', {
        hasProjectId: !!projectId,
        hasApiKey: !!apiKey,
      });
    }
  }

  return config;
}

/**
 * Determines if Keycloak authentication should be enabled
 *
 * Helper function to check if Keycloak SSO is enabled in the current
 * environment. Use this for conditional Keycloak initialization.
 *
 * @returns True if Keycloak should be enabled
 *
 * @example
 * ```typescript
 * if (shouldEnableKeycloak()) {
 *   const keycloak = new Keycloak(keycloakConfig);
 *   await keycloak.init();
 * }
 * ```
 */
export function shouldEnableKeycloak(): boolean {
  const config = getAuthConfig();
  const enabled = config.allowKeycloak;

  console.log(`[AuthConfig] Keycloak enabled: ${enabled}`);
  return enabled;
}

/**
 * Determines if Firebase authentication should be enabled
 *
 * Helper function to check if Firebase Auth is enabled in the current
 * environment. Use this for conditional Firebase initialization.
 *
 * @returns True if Firebase should be enabled
 *
 * @example
 * ```typescript
 * if (shouldEnableFirebase()) {
 *   const app = initializeApp(firebaseConfig);
 *   const auth = getAuth(app);
 * }
 * ```
 */
export function shouldEnableFirebase(): boolean {
  const config = getAuthConfig();
  const enabled = config.allowFirebase;

  console.log(`[AuthConfig] Firebase enabled: ${enabled}`);
  return enabled;
}

/**
 * Determines if Google OAuth authentication should be enabled
 *
 * Helper function to check if Google OAuth sign-in is enabled in the current
 * environment. Use this to conditionally show/hide Google sign-in buttons.
 *
 * @returns True if Google OAuth should be enabled
 *
 * @example
 * ```typescript
 * {shouldEnableGoogle() && (
 *   <GoogleSignInButton />
 * )}
 * ```
 */
export function shouldEnableGoogle(): boolean {
  const config = getAuthConfig();
  const enabled = config.allowGoogle;

  console.log(`[AuthConfig] Google OAuth enabled: ${enabled}`);
  return enabled;
}

/**
 * Validates the authentication configuration
 *
 * Performs comprehensive validation of the authentication configuration
 * to ensure all required credentials are present for enabled providers.
 * Should be called during application startup to fail fast if configuration
 * is invalid.
 *
 * Validation Rules:
 * 1. At least one authentication method must be enabled
 * 2. If Keycloak is enabled, all Keycloak credentials must be present
 * 3. If Firebase is enabled, all Firebase credentials must be present
 * 4. The primary provider must be enabled
 *
 * @throws {AuthConfigError} If configuration is invalid
 *
 * @example
 * ```typescript
 * try {
 *   validateAuthConfig();
 *   console.log('Auth configuration is valid');
 * } catch (error) {
 *   console.error('Invalid auth configuration:', error.message);
 *   process.exit(1);
 * }
 * ```
 */
export function validateAuthConfig(): void {
  console.log('[AuthConfig] Validating authentication configuration...');

  const config = getAuthConfig();
  const errors: string[] = [];

  // Rule 1: At least one auth method must be enabled
  if (!config.allowKeycloak && !config.allowFirebase) {
    errors.push(
      'At least one authentication method must be enabled. ' +
      'Set VITE_ALLOW_KEYCLOAK=true or VITE_ALLOW_FIREBASE=true'
    );
  }

  // Rule 2: Validate Keycloak configuration if enabled
  if (config.allowKeycloak) {
    console.log('[AuthConfig] Validating Keycloak configuration...');

    if (!config.keycloak) {
      errors.push('Keycloak is enabled but configuration is missing');
    } else {
      const missing: string[] = [];

      if (!config.keycloak.baseUrl) {
        missing.push('VITE_KEYCLOAK_BASE_URL');
      }
      if (!config.keycloak.realm) {
        missing.push('VITE_KEYCLOAK_REALM');
      }
      if (!config.keycloak.clientId) {
        missing.push('VITE_KEYCLOAK_CLIENT_ID');
      }

      if (missing.length > 0) {
        errors.push(
          `Keycloak is enabled but required environment variables are missing: ${missing.join(', ')}`
        );
      } else {
        console.log('[AuthConfig] ✓ Keycloak configuration valid');
      }
    }
  }

  // Rule 3: Validate Firebase configuration if enabled
  if (config.allowFirebase) {
    console.log('[AuthConfig] Validating Firebase configuration...');

    if (!config.firebase) {
      errors.push('Firebase is enabled but configuration is missing');
    } else {
      const missing: string[] = [];

      if (!config.firebase.projectId) {
        missing.push('VITE_FIREBASE_PROJECT_ID');
      }
      if (!config.firebase.apiKey) {
        missing.push('VITE_FIREBASE_API_KEY');
      }

      if (missing.length > 0) {
        errors.push(
          `Firebase is enabled but required environment variables are missing: ${missing.join(', ')}`
        );
      } else {
        console.log('[AuthConfig] ✓ Firebase configuration valid');
      }
    }
  }

  // Rule 4: Validate primary provider is enabled
  if (config.provider === 'keycloak' && !config.allowKeycloak) {
    errors.push(
      'VITE_AUTH_PROVIDER is set to "keycloak" but Keycloak is not properly configured'
    );
  }

  if (config.provider === 'firebase' && !config.allowFirebase) {
    errors.push(
      'VITE_AUTH_PROVIDER is set to "firebase" but Firebase is not properly configured'
    );
  }

  if (config.provider === 'hybrid' && (!config.allowKeycloak || !config.allowFirebase)) {
    errors.push(
      'VITE_AUTH_PROVIDER is set to "hybrid" but both Firebase and Keycloak must be properly configured'
    );
  }

  // Throw error if validation failed
  if (errors.length > 0) {
    const errorMessage = [
      'Authentication configuration validation failed:',
      ...errors.map((err, i) => `  ${i + 1}. ${err}`),
      '',
      'Please check your .env file and ensure all required environment variables are set.',
    ].join('\n');

    console.error('[AuthConfig] ✗ Validation failed:', errorMessage);
    throw new AuthConfigError(errorMessage);
  }

  console.log('[AuthConfig] ✓ Authentication configuration is valid');
  console.log('[AuthConfig] Summary:', {
    primaryProvider: config.provider,
    enabledMethods: [
      config.allowKeycloak && 'Keycloak',
      config.allowFirebase && 'Firebase',
      config.allowGoogle && 'Google OAuth',
    ].filter(Boolean),
  });
}

/**
 * Gets a human-readable description of the current auth configuration
 *
 * Useful for debugging and logging to understand what authentication
 * methods are available in the current environment.
 *
 * @returns Formatted configuration summary
 *
 * @example
 * ```typescript
 * console.log(getAuthConfigSummary());
 * // Output:
 * // Authentication Configuration:
 * //   Primary Provider: firebase
 * //   Enabled Methods: Firebase, Google OAuth
 * ```
 */
export function getAuthConfigSummary(): string {
  const config = getAuthConfig();
  const methods: string[] = [];

  if (config.allowKeycloak) methods.push('Keycloak SSO');
  if (config.allowFirebase) methods.push('Firebase Auth');
  if (config.allowGoogle) methods.push('Google OAuth');

  return [
    'Authentication Configuration:',
    `  Primary Provider: ${config.provider}`,
    `  Enabled Methods: ${methods.join(', ') || 'None'}`,
  ].join('\n');
}
